import { useState, useEffect, useMemo } from "react";
import { ScheduleSection } from "@/components/schedule/ScheduleSection";
import { useNavigate } from "react-router-dom";
import { api, toast } from "../App";
import Layout from "@/components/Layout";
import { PeriodJumpPicker } from "@/components/PeriodJumpPicker";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ChevronLeft,
  ChevronRight,
  Calculator,
  Briefcase,
  Receipt,
  CircleDollarSign,
  FileEdit,
  ExternalLink,
  AlertTriangle,
  CalendarClock,
  CalendarOff,
  Check,
} from "lucide-react";

// ── Helpers ──────────────────────────────────────────────────────────
const todayISO = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const fmtUSD = (n) =>
  `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtCAD = (n) =>
  `C$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

// Shift the anchor date by ±1 period (month / quarter / year).
const shiftAnchor = (anchor, period, delta) => {
  const [y, m, d] = anchor.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  if (period === "month") date.setMonth(date.getMonth() + delta);
  else if (period === "quarter") date.setMonth(date.getMonth() + delta * 3);
  else date.setFullYear(date.getFullYear() + delta);
  const yy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
};

// Card metadata
const CARD_META = {
  estimations: {
    title: "Estimations",
    Icon: Calculator,
    accent: "from-amber-50 to-amber-100 border-amber-200",
    iconBg: "bg-amber-500",
    drillTitle: "Estimations submitted",
    columns: [
      { key: "estimation_number", label: "Estimation #" },
      { key: "project_name", label: "Project Name" },
      { key: "client_name", label: "Client" },
      { key: "submission_status", label: "Submission" },
      { key: "award_status", label: "Award" },
      { key: "created_at", label: "Date" },
      { key: "value", label: "Value", numeric: true },
    ],
  },
  projects: {
    title: "Projects",
    Icon: Briefcase,
    accent: "from-blue-50 to-blue-100 border-blue-200",
    iconBg: "bg-blue-500",
    drillTitle: "Projects booked",
    columns: [
      { key: "project_number", label: "Project #" },
      { key: "name", label: "Name" },
      { key: "client_name", label: "Client" },
      { key: "status", label: "Status" },
      { key: "start_date", label: "Start Date" },
      { key: "awarded_date", label: "Awarded Date" },
      { key: "total_value", label: "Project Value", numeric: true },
      { key: "value", label: "Booked This Period", numeric: true },
    ],
  },
  invoiced: {
    title: "Invoiced",
    Icon: Receipt,
    accent: "from-violet-50 to-violet-100 border-violet-200",
    iconBg: "bg-violet-500",
    drillTitle: "Invoices issued",
    columns: [
      { key: "invoice_number", label: "Invoice #" },
      { key: "project_number", label: "Project #" },
      { key: "project_name", label: "Project Name" },
      { key: "client_name", label: "Client" },
      { key: "payment_status", label: "Status" },
      { key: "issue_date", label: "Issue Date" },
      { key: "value", label: "Amount", numeric: true },
    ],
  },
  collected: {
    title: "Collected",
    Icon: CircleDollarSign,
    accent: "from-emerald-50 to-emerald-100 border-emerald-200",
    iconBg: "bg-emerald-500",
    drillTitle: "Payments received",
    columns: [
      { key: "invoice_number", label: "Invoice #" },
      { key: "project_number", label: "Project #" },
      { key: "client_name", label: "Client" },
      { key: "payment_mode", label: "Mode" },
      { key: "payment_reference", label: "Reference" },
      { key: "date_received", label: "Received" },
      { key: "value", label: "Amount", numeric: true },
    ],
  },
  change_orders: {
    title: "Change Orders (Approved)",
    Icon: FileEdit,
    accent: "from-rose-50 to-rose-100 border-rose-200",
    iconBg: "bg-rose-500",
    drillTitle: "Approved Change Orders",
    columns: [
      { key: "change_order_number", label: "CO #" },
      { key: "project_number", label: "Project #" },
      { key: "client_name", label: "Client" },
      { key: "description", label: "Description" },
      { key: "time_impact", label: "Hrs", numeric: true },
      { key: "approved_date", label: "Approved" },
      { key: "value", label: "Approved Value", numeric: true },
    ],
  },
};

const CARD_ORDER = ["estimations", "projects", "change_orders", "invoiced", "collected"];

// ── Component ────────────────────────────────────────────────────────
export default function Dashboard({ user }) {
  const [period, setPeriod] = useState("month");
  const [anchor, setAnchor] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillKey, setDrillKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(
          `/dashboard/financial-summary?period=${period}&date=${anchor}`
        );
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) toast.error("Failed to load dashboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [period, anchor]);

  const cards = data?.cards || {};
  const cadRate = data?.cad_to_usd || 0.74;
  const periodLabel = data?.period?.label || "—";
  const comparison = data?.comparison || null;

  const totalUSDAcrossAll = useMemo(() => {
    // Grand total = Projects (booking value) + approved Change Orders only.
    // Estimations / Invoiced / Collected are visibility KPIs, not booking value.
    return (cards?.projects?.total_usd || 0) + (cards?.change_orders?.total_usd || 0);
  }, [cards]);

  return (
    <Layout user={user}>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Page Header */}
        <div>
          <h1 className="text-md3-display-small text-md3-on-surface mb-1">
            Dashboard
          </h1>
          <p className="text-md3-body-large text-md3-on-surface-variant">
            Welcome back, {user?.first_name} {user?.last_name || ""}
          </p>
        </div>

        {/* ─────────────── BLOCK 1 · Financial Overview ─────────────── */}
        <section
          className="rounded-md3-xl border border-md3-outline-variant bg-md3-surface-container-lowest shadow-md3-1 p-5 md:p-6 space-y-5"
          data-testid="dash-block-financial"
        >
          {/* Block header + period filter */}
          <div className="flex flex-wrap items-center justify-between gap-4 pb-3 border-b border-md3-outline-variant/60">
            <div>
              <h2 className="text-md3-title-large font-semibold text-md3-on-surface">
                Financial Overview
              </h2>
              <p className="text-md3-body-small text-md3-on-surface-variant">
                Booking value, invoicing, and collections for the selected period
              </p>
            </div>

            {/* Period selector */}
            <div className="flex items-center gap-3">
              <Tabs value={period} onValueChange={setPeriod} data-testid="dash-period-tabs">
                <TabsList>
                  <TabsTrigger value="month" data-testid="dash-period-month">Month</TabsTrigger>
                  <TabsTrigger value="quarter" data-testid="dash-period-quarter">Quarter</TabsTrigger>
                  <TabsTrigger value="year" data-testid="dash-period-year">Year</TabsTrigger>
                </TabsList>
              </Tabs>

              <div className="flex items-center gap-1 bg-md3-surface-container rounded-md3-md p-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAnchor(shiftAnchor(anchor, period, -1))}
                  data-testid="dash-period-prev"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <PeriodJumpPicker
                  period={period}
                  anchor={new Date(...anchor.split("-").map((n, i) => Number(n) - (i === 1 ? 1 : 0)))}
                  label={periodLabel}
                  testIdPrefix="dash"
                  onPick={(d) => setAnchor(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`)}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAnchor(shiftAnchor(anchor, period, +1))}
                  data-testid="dash-period-next"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-1"
                  onClick={() => setAnchor(todayISO())}
                  data-testid="dash-period-today"
                >
                  Today
                </Button>
              </div>
            </div>
          </div>

          {/* Grand total banner */}
          {loading ? (
            <div
              className="rounded-md3-lg border border-md3-outline-variant bg-md3-surface-container px-5 py-3 flex items-center justify-between shadow-md3-1 animate-pulse"
              data-testid="dash-grand-total-skeleton"
            >
              <div className="h-4 w-64 bg-md3-surface-container-high rounded-md3-sm" />
              <div className="h-6 w-56 bg-md3-surface-container-high rounded-md3-sm" />
            </div>
          ) : (
          <div className="rounded-md3-lg border border-md3-outline-variant bg-md3-surface-container px-5 py-3 flex items-center justify-between shadow-md3-1">
            <div className="text-md3-body-medium text-md3-on-surface-variant">
              <span className="font-medium">Period:</span> {periodLabel}
              <span className="mx-3 text-md3-outline">·</span>
              <span className="font-medium">CAD-USD rate:</span> {cadRate.toFixed(4)}
            </div>
            <div className="text-right">
              <div className="text-md3-headline-small font-bold text-md3-on-surface" data-testid="dash-grand-total">
                {fmtUSD(totalUSDAcrossAll)}{" "}
                <span className="text-md3-body-small font-normal text-md3-on-surface-variant">
                  booking total (USD) — Projects + Approved COs
                </span>
              </div>
              {comparison && comparison.delta_pct !== null && comparison.previous_total_usd > 0 && (
                <div
                  className={`text-xs italic mt-0.5 ${
                    comparison.delta_pct >= 0
                      ? "text-emerald-600/80"
                      : "text-rose-600/80"
                  }`}
                  data-testid="dash-yoy-delta"
                >
                  {comparison.delta_pct >= 0 ? "▲" : "▼"} {Math.abs(comparison.delta_pct).toFixed(1)}% vs {comparison.previous_label}{" "}
                  <span className="text-md3-on-surface-variant/70 not-italic">
                    ({fmtUSD(comparison.previous_total_usd)})
                  </span>
                </div>
              )}
            </div>
          </div>
          )}

          {/* KPI cards grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5" data-testid="dash-kpi-skeleton">
              {CARD_ORDER.map((k) => (
                <KpiCardSkeleton key={k} cardKey={k} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
              {CARD_ORDER.map((k) => (
                <KpiCard
                  key={k}
                  cardKey={k}
                  bucket={cards[k]}
                  onDrill={() => setDrillKey(k)}
                />
              ))}
            </div>
          )}
        </section>
        {/* ─────────────── END BLOCK 1 ─────────────── */}

        {/* ─────────────── BLOCK 2 · Schedule Overview ─────────────── */}
        <ScheduleOverviewBlock user={user} />
        {/* ─────────────── END BLOCK 2 ─────────────── */}
      </div>

      {/* Drill-down dialog */}
      <DrillDialog
        open={!!drillKey}
        onClose={() => setDrillKey(null)}
        cardKey={drillKey}
        bucket={drillKey ? cards[drillKey] : null}
        periodLabel={periodLabel}
        cadRate={cadRate}
      />
    </Layout>
  );
}

// ── KPI Card Skeleton ───────────────────────────────────────────────
function KpiCardSkeleton({ cardKey }) {
  const meta = CARD_META[cardKey] || {};
  const { accent = "from-slate-50 to-slate-100 border-slate-200", iconBg = "bg-slate-300" } = meta;
  return (
    <Card
      className={`shadow-md3-1 rounded-md3-lg border bg-gradient-to-br ${accent} animate-pulse`}
      data-testid={`dash-card-${cardKey}-skeleton`}
    >
      <CardHeader className="flex flex-row items-center justify-between pb-2 pt-4 px-4">
        <div className="h-3 w-24 bg-white/60 rounded-md3-sm" />
        <div className={`${iconBg} opacity-60 p-2 rounded-md3-md shadow-md3-1 h-8 w-8`} />
      </CardHeader>
      <CardContent className="pb-4 px-4">
        <div className="flex items-baseline gap-2 mb-3">
          <div className="h-9 w-14 bg-white/70 rounded-md3-sm" />
          <div className="h-3 w-14 bg-white/50 rounded-md3-sm" />
        </div>
        <div className="space-y-2 mb-3">
          <div className="flex items-center justify-between">
            <div className="h-3 w-14 bg-white/50 rounded-md3-sm" />
            <div className="h-3 w-20 bg-white/70 rounded-md3-sm" />
          </div>
          <div className="flex items-center justify-between">
            <div className="h-3 w-14 bg-white/40 rounded-md3-sm" />
            <div className="h-3 w-16 bg-white/60 rounded-md3-sm" />
          </div>
        </div>
        <div className="border-t border-md3-outline-variant/60 pt-2 flex items-baseline justify-between">
          <div className="h-3 w-16 bg-white/50 rounded-md3-sm" />
          <div className="h-4 w-24 bg-white/70 rounded-md3-sm" />
        </div>
      </CardContent>
    </Card>
  );
}


// ── Schedule Section Skeleton ───────────────────────────────────────
function ScheduleSectionSkeleton({ tone = "slate" }) {
  const toneMap = {
    rose: "bg-rose-50 border-rose-200",
    amber: "bg-amber-50 border-amber-200",
    slate: "bg-slate-50 border-slate-200",
  };
  return (
    <div
      className={`rounded-md3-lg border ${toneMap[tone]} px-4 py-3 flex items-center justify-between animate-pulse`}
      data-testid={`sched-skeleton-${tone}`}
    >
      <div className="flex items-center gap-3">
        <div className="h-8 w-8 rounded-md3-md bg-white/70" />
        <div className="space-y-2">
          <div className="h-3 w-40 bg-white/70 rounded-md3-sm" />
          <div className="h-3 w-56 bg-white/50 rounded-md3-sm" />
        </div>
      </div>
      <div className="h-4 w-4 bg-white/60 rounded-md3-sm" />
    </div>
  );
}


// ── KPI Card ─────────────────────────────────────────────────────────
function KpiCard({ cardKey, bucket, onDrill }) {
  const meta = CARD_META[cardKey];
  if (!meta) return null;
  const { Icon, title, accent, iconBg } = meta;
  const count = bucket?.count || 0;
  const usd = bucket?.by_currency?.USD || { count: 0, value: 0 };
  const cad = bucket?.by_currency?.CAD || { count: 0, value: 0 };
  const totalUSD = bucket?.total_usd || 0;
  const hasCAD = cad.count > 0;

  return (
    <Card
      className={`shadow-md3-1 hover:shadow-md3-3 transition-all duration-200 rounded-md3-lg border bg-gradient-to-br ${accent} cursor-pointer group`}
      onClick={onDrill}
      data-testid={`dash-card-${cardKey}`}
    >
      <CardHeader className="flex flex-row items-center justify-between pb-2 pt-4 px-4">
        <CardTitle className="text-md3-label-large font-semibold text-md3-on-surface-variant">
          {title}
        </CardTitle>
        <div className={`${iconBg} p-2 rounded-md3-md shadow-md3-1`}>
          <Icon className="h-4 w-4 text-white" />
        </div>
      </CardHeader>
      <CardContent className="pb-4 px-4">
        {/* Count */}
        <div className="flex items-baseline gap-2 mb-2">
          <div
            className="text-md3-display-small font-bold text-md3-on-surface leading-none"
            data-testid={`dash-card-${cardKey}-count`}
          >
            {count}
          </div>
          <div className="text-md3-body-small text-md3-on-surface-variant">
            {count === 1 ? "record" : "records"}
          </div>
        </div>

        {/* Currency breakdown */}
        <div className="space-y-1 mb-3">
          <div className="flex items-center justify-between text-md3-body-small">
            <span className="text-md3-on-surface-variant">
              USD <span className="text-md3-outline">·</span> {usd.count}
            </span>
            <span className="font-mono font-semibold text-md3-on-surface">
              {fmtUSD(usd.value)}
            </span>
          </div>
          {hasCAD && (
            <div className="flex items-center justify-between text-md3-body-small">
              <span className="text-md3-on-surface-variant">
                CAD <span className="text-md3-outline">·</span> {cad.count}
              </span>
              <span className="font-mono font-semibold text-md3-on-surface">
                {fmtCAD(cad.value)}
              </span>
            </div>
          )}
        </div>

        {/* Total in USD */}
        <div className="border-t border-md3-outline-variant/60 pt-2 flex items-baseline justify-between">
          <span className="text-md3-label-small text-md3-on-surface-variant uppercase tracking-wide">
            Total (USD)
          </span>
          <span
            className="text-md3-headline-small font-bold text-md3-on-surface"
            data-testid={`dash-card-${cardKey}-total-usd`}
          >
            {fmtUSD(totalUSD)}
          </span>
        </div>

        {/* View details affordance */}
        <div className="mt-3 flex items-center justify-end text-md3-label-small text-md3-primary opacity-0 group-hover:opacity-100 transition-opacity">
          <ExternalLink className="h-3 w-3 mr-1" />
          View details
        </div>
      </CardContent>
    </Card>
  );
}

// ── Drill-down dialog ────────────────────────────────────────────────
function DrillDialog({ open, onClose, cardKey, bucket, periodLabel, cadRate }) {
  if (!cardKey) return null;
  const meta = CARD_META[cardKey];
  const items = bucket?.items || [];
  const usd = bucket?.by_currency?.USD || { count: 0, value: 0 };
  const cad = bucket?.by_currency?.CAD || { count: 0, value: 0 };

  const renderCell = (item, col) => {
    const v = item[col.key];
    if (v === null || v === undefined || v === "") return "—";
    if (col.key === "value" || col.key === "total_value") {
      const isCAD = item.currency === "CAD";
      return isCAD ? fmtCAD(v) : fmtUSD(v);
    }
    if (col.key === "created_at" || col.key === "issue_date" || col.key === "date_received" || col.key === "approved_date" || col.key === "start_date" || col.key === "awarded_date") {
      const s = String(v);
      return s.length >= 10 ? s.slice(0, 10) : s;
    }
    if (
      col.key === "submission_status" ||
      col.key === "award_status" ||
      col.key === "payment_status" ||
      col.key === "status"
    ) {
      return (
        <Badge variant="outline" className="capitalize">
          {String(v).replace(/_/g, " ")}
        </Badge>
      );
    }
    return String(v);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <meta.Icon className="h-5 w-5" />
            {meta.drillTitle} · {periodLabel}
          </DialogTitle>
          <DialogDescription>
            {bucket?.count || 0} records · USD {fmtUSD(usd.value)}
            {cad.count > 0 && (
              <>
                {" · "}CAD {fmtCAD(cad.value)} ({fmtUSD(cad.value * cadRate)} USD @ {cadRate.toFixed(4)})
              </>
            )}
            {" · "}Total {fmtUSD(bucket?.total_usd || 0)} USD
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-auto flex-1" data-testid={`dash-drill-${cardKey}`}>
          {items.length === 0 ? (
            <div className="py-10 text-center text-md3-on-surface-variant">
              No records in this period.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {meta.columns.map((c) => (
                    <TableHead key={c.key} className={c.numeric ? "text-right" : ""}>
                      {c.label}
                    </TableHead>
                  ))}
                  <TableHead className="text-right">Cur</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it, idx) => (
                  <TableRow key={`${it.id || "row"}-${idx}`} data-testid={`dash-drill-row-${idx}`}>
                    {meta.columns.map((c) => (
                      <TableCell key={c.key} className={c.numeric ? "text-right font-mono" : ""}>
                        {renderCell(it, c)}
                      </TableCell>
                    ))}
                    <TableCell className="text-right">
                      <Badge
                        variant="outline"
                        className={
                          it.currency === "CAD"
                            ? "border-rose-300 text-rose-700"
                            : "border-emerald-300 text-emerald-700"
                        }
                      >
                        {it.currency}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ── Schedule Overview Block ──────────────────────────────────────────
function ScheduleOverviewBlock() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/dashboard/schedule-overview");
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) toast.error("Failed to load schedule overview");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const counts = data?.counts || { overdue: 0, today_tomorrow: 0, unplanned: 0 };
  const totalActionable = counts.overdue + counts.today_tomorrow + counts.unplanned;

  const rowClick = (projectId) => {
    if (projectId) navigate(`/projects/${projectId}`);
  };

  return (
    <section
      className="rounded-md3-xl border border-md3-outline-variant bg-md3-surface-container-lowest shadow-md3-1 p-5 md:p-6 space-y-5"
      data-testid="dash-block-schedule"
    >
      {/* Block header */}
      <div className="flex flex-wrap items-center justify-between gap-4 pb-3 border-b border-md3-outline-variant/60">
        <div>
          <h2 className="text-md3-title-large font-semibold text-md3-on-surface">
            Schedule Overview
          </h2>
          <p className="text-md3-body-small text-md3-on-surface-variant">
            Live snapshot · {data?.as_of ? `As of ${data.as_of}` : ""} · {totalActionable} actionable submittals
          </p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3" data-testid="dash-schedule-skeleton">
          {[0, 1, 2].map((i) => (
            <ScheduleSectionSkeleton key={i} tone={["rose", "amber", "slate"][i]} />
          ))}
        </div>
      ) : (
        <div className="space-y-5">
          <ScheduleSection
            testid="sched-overdue"
            Icon={AlertTriangle}
            title="Overdue"
            subtitle="Planned date has passed, action not yet taken"
            accentBg="bg-rose-50 border-rose-200"
            accentIcon="text-rose-600 bg-rose-100"
            countBadge="bg-rose-600 text-white"
            rows={data?.overdue || []}
            emptyText="No overdue schedules — you're caught up."
            showOverdueDays
            rowClick={rowClick}
          />
          <ScheduleSection
            testid="sched-today-tomorrow"
            Icon={CalendarClock}
            title="Planned for Today / Tomorrow"
            subtitle={`${data?.today || ""} · ${data?.tomorrow || ""}`}
            accentBg="bg-amber-50 border-amber-200"
            accentIcon="text-amber-700 bg-amber-100"
            countBadge="bg-amber-600 text-white"
            rows={data?.today_tomorrow || []}
            emptyText="Nothing scheduled for today or tomorrow."
            rowClick={rowClick}
          />
          <ScheduleSection
            testid="sched-unplanned"
            Icon={CalendarOff}
            title="Unplanned"
            subtitle="Active step has no planned date"
            accentBg="bg-slate-50 border-slate-200"
            accentIcon="text-slate-600 bg-slate-200"
            countBadge="bg-slate-600 text-white"
            rows={data?.unplanned || []}
            emptyText="All active steps have a planned date."
            rowClick={rowClick}
          />
        </div>
      )}
    </section>
  );
}

